import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonInput,
  IonSpinner,
  IonText,
  IonAlert,
} from '@ionic/react';
import { hasComponent } from '../../domain/Component';
import { THRESHOLD_SECTIONS, thresholdValue, type ThresholdSectionInfo } from '../../domain/Thresholds';
import type { Device } from '../../domain/Device';
import { useStationThresholds } from '../../application/use-cases/devices/useStationThresholds';
import './StationThresholds.css';

function applies(section: ThresholdSectionInfo, device: Device): boolean {
  if (section.requires === 'soil') return hasComponent(device.components, 'soil');
  if (section.requires === 'air+wind') return hasComponent(device.components, 'air') && hasComponent(device.components, 'wind');
  return hasComponent(device.components, 'air');
}

const formatDefault = (n: number) => String(n).replace('.', ',');

// Criterios con los que la app interpreta las lecturas de esta estación
// (ventana de pulverización, helada, humedad, suelo). Solo se muestran las
// secciones que aplican a lo que tiene enchufado.
const StationThresholds: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { device, draft, defaults, custom, loading, saving, error, saved, updateField, handleSave, handleReset } = useStationThresholds(id);
  const [confirmReset, setConfirmReset] = useState(false);
  const sections = device ? THRESHOLD_SECTIONS.filter((s) => applies(s, device)) : [];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/devices/${id}`} />
          </IonButtons>
          <IonTitle>Criterios de la estación</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && device && draft && defaults && (
          <>
            <IonText color="medium">
              <p className="thresholds-intro">
                {device.name}: con estos valores la app decide si conviene pulverizar, avisa riesgo de helada y califica la
                humedad. Vienen con valores de referencia; ajustalos a tu cultivo y tu forma de trabajar. El asistente de IA
                usa los mismos.
              </p>
            </IonText>

            {sections.length === 0 && (
              <IonText color="medium">
                <p>Esta estación todavía no tiene sensores con criterios para ajustar (clima, viento o sondas de suelo).</p>
              </IonText>
            )}

            {sections.map((section) => (
              <IonCard key={section.section}>
                <IonCardContent>
                  <p className="thresholds-title">{section.title}</p>
                  <p className="thresholds-description">{section.description}</p>
                  <div className="thresholds-fields">
                    {section.fields.map((field) => {
                      const def = thresholdValue(defaults, section.section, field.key);
                      const value = draft[section.section][field.key];
                      const changed = Number(value.replace(',', '.')) !== def;
                      return (
                        <label className={`thresholds-field ${changed ? 'changed' : ''}`} key={field.key}>
                          <span className="thresholds-field-label">
                            {field.label}
                            <small>Referencia: {formatDefault(def)}{field.unit ? ` ${field.unit}` : ''}</small>
                          </span>
                          <span className="thresholds-field-input">
                            <IonInput
                              aria-label={field.label}
                              inputmode="decimal"
                              value={value}
                              onIonInput={(e) => updateField(section.section, field.key, e.detail.value ?? '')}
                            />
                            <span className="thresholds-unit">{field.unit}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </IonCardContent>
              </IonCard>
            ))}

            {error && (
              <IonText color="danger">
                <p style={{ fontSize: 13 }}>{error}</p>
              </IonText>
            )}
            {saved && !error && (
              <IonText color="success">
                <p style={{ fontSize: 13 }}>Guardado.</p>
              </IonText>
            )}

            {sections.length > 0 && (
              <IonButton
                expand="block"
                disabled={saving || device.access === 'view'}
                onClick={async () => {
                  if (await handleSave()) history.goBack();
                }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </IonButton>
            )}
            {custom && device.access !== 'view' && (
              <IonButton expand="block" fill="outline" color="medium" disabled={saving} onClick={() => setConfirmReset(true)}>
                Volver a los valores de referencia
              </IonButton>
            )}
          </>
        )}

        {!loading && !device && error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        <IonAlert
          isOpen={confirmReset}
          onDidDismiss={() => setConfirmReset(false)}
          header="¿Volver a los valores de referencia?"
          message="Se pierden los criterios que ajustaste para esta estación."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Restaurar', role: 'destructive', handler: handleReset },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default StationThresholds;
